import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { imageConfigs } from 'shared/constants';
import defaultImage from 'assets/images/default-book.png';
import {
  addCartItem,
  addWishlistItem,
  removeWishlistItem,
  removeCartItem,
  fetchCartItems,
  fetchCartCount,
} from 'store/actions';
import { useLocalState } from 'shared/hooks';
import { ReactComponent as CartLogo } from 'assets/images/svg/add-cart.svg';
import { ReactComponent as WishlistLogo } from 'assets/images/svg/wishlist.svg';
import { ReactComponent as WishlistLogoFilled } from 'assets/images/svg/wishlist-filled.svg';
import { ReactComponent as CartDelete } from 'assets/images/svg/cart-alt.svg';
import { ImageWithLoading } from 'shared/components';
import ReactTooltip from 'react-tooltip';
import switchCaseError from 'shared/utils/switchCaseError';
import { toast } from 'react-toastify';
import { useState } from 'react';

const { IMAGE_URL } = imageConfigs;

const BookSliderItem = ({ bookData, wishlist, donation }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const user = useSelector((state) => state.auth.user);
  const itemAdding = useSelector((state) => state.cart.itemAdding);
  const cartError = useSelector((state) => state.cart.error);
  const cartData = useSelector((state) => state.cart.cartData);
  const wishlisting = useSelector((state) => state.wishlist.itemAdding);
  const wishlistError = useSelector((state) => state.wishlist.error);
  const wishlistRemoving = useSelector((state) => state.wishlist.removing);

  const {
    bookTitle,
    bookCover,
    bookId,
    wishlistItemId,
    borrowCount,
    available = true,
    cartEnabled = true,
    wishlisted = false,
    alreadyRead = false,
    addedToCart = false,
    tooloNominates = false,
    highMatched = false,
    cartItemId: initialCartItemId,
  } = bookData;

  // Find cartItemId from Redux cartData if present
  const getCartItemId = () => {
    for (const child of cartData) {
      if (Array.isArray(child.books)) {
        const found = child.books.find((b) => b.bookId === bookId);
        if (found && found.cartItemId) return found.cartItemId;
      }
    }
    return initialCartItemId || null;
  };
  const [cartItemId, setCartItemId] = useLocalState(
    getCartItemId(),
    false,
    cartError,
    itemAdding
  );
  // Local state to track if just added to cart
  const [addedNow, setAddedNow] = useLocalState(false);
  const [wishlistedNow, changeWishlistSubmit] = useLocalState(
    wishlisted,
    undefined,
    wishlistError,
    wishlisting
  );
  // Show remove if cartItemId exists
  const isInCart = !!cartItemId;
  const imageUrl = bookCover ? `${IMAGE_URL}/${bookCover}` : defaultImage;

  //const secondaryIcon = wishlist ? removeIcon : wishlistIcon;

  const handleAddToCartClicked = async () => {
    const action = await dispatch(addCartItem({ bookId }));
    if (action?.payload?._id) {
      setCartItemId(action.payload._id);
      setAddedNow(true);
    }
    setTimeout(() => {
      dispatch(fetchCartItems());
      dispatch(fetchCartCount());
    }, 500);
    if (wishlist) {
      handleRemoveWishlistItem();
    }
  };

  const [localCartEnabled, setLocalCartEnabled] = useState(cartEnabled);

  const handleRemoveFromCartClicked = async () => {
    const result = await dispatch(removeCartItem({ cartItemId }));
    setCartItemId(null);
    setAddedNow(false);
    setLocalCartEnabled(true); // Always enable add to cart after removal
    if (!wishlisted && !wishlist) {
      changeWishlistSubmit(false);
    }
    setTimeout(() => {
      setCartItemId(getCartItemId());
      setAddedNow(false);
      setLocalCartEnabled(true); // Ensure enabled after Redux update
      dispatch(fetchCartItems());
      dispatch(fetchCartCount());
    }, 500);
    if (!cartError && (!result?.payload || !result.payload.message)) {
      toast.success('Book removed from cart successfully.');
    }
  };

  const onImageClicked = () => {
    window.open(`/book-details/${bookId}`, '_blank');
  };

  const handleAddToWishListClicked = () => {
    dispatch(addWishlistItem({ bookId }));
    changeWishlistSubmit(true);
  };

  const handleRemoveWishlistItem = () => {
    dispatch(removeWishlistItem({ wishlistItemId }));
    changeWishlistSubmit(false);
  };

  const redirectLoginPage = () => {
    history.push(switchCaseError('cart').path);
    toast.error(switchCaseError('cart').message);
  };

  const wishListRedirectToLoginPage = () => {
    history.push(switchCaseError('wishList').path);
    toast.error(switchCaseError('wishList').message);
  };

  // Use localCartEnabled for button and message
  let cartMessage;
  if (!available) {
    cartMessage = 'Unavailable';
  } else if (isInCart) {
    cartMessage = 'Remove from cart';
  } else if (!localCartEnabled) {
    cartMessage = 'Currently reading';
  } else {
    cartMessage = 'Add to cart';
  }

  return (
    <div className="slide">
      <div className="eachbook">
        <div>
          <div
            className="bookcoverrest"
            onClick={onImageClicked}
            style={{ cursor: 'pointer' }}
          >
            <ReactTooltip id="nominate-check" place="bottom">
              <span>Töölö recommends</span>
            </ReactTooltip>
            {tooloNominates && !wishlist && !donation && (
              <div
                className="toolo-nominates"
                data-tip={true}
                data-for="nominate-check"
              >
                <i className="fas fa-crown"></i>
              </div>
            )}
            <ImageWithLoading
              src={imageUrl}
              alt={bookTitle}
              className="img-responsive"
            />
            {!available && <div className="isfined">Unavailable</div>}
          </div>
          {wishlisted ? (
            <div className="onlyforwishlist" style={{ padding: '2px 5px' }}>
              <p className="authorname">{bookTitle}</p>
            </div>
          ) : (
            ''
          )}

          <div className="bbokrestinfo">
            <p className="authorname">{bookTitle}</p>
            {/* <div className="ratingn">
              <div className="Stars" style={{ '--rating': 4 }} />
            </div> */}
            {donation ? (
              <div
                className={`actionbuttons ${
                  isInCart || addedNow || wishlistedNow || wishlist
                    ? 'addedcf'
                    : ''
                }`}
                style={{
                  fontSize: '23px',
                }}
              >
                Borrow Count: {borrowCount}
              </div>
            ) : (
              <div
                className={`actionbuttons ${
                  isInCart || addedNow || wishlistedNow || wishlist
                    ? 'addedcf'
                    : ''
                }`}
              >
                {isInCart ? (
                  <button
                    type="button"
                    className="addcartlt removecartlt"
                    disabled={itemAdding === bookId || !available}
                    onClick={
                      !user ? redirectLoginPage : handleRemoveFromCartClicked
                    }
                    data-meaning={cartMessage}
                  >
                    <CartDelete />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="addcartlt"
                    disabled={
                      !available || !localCartEnabled || itemAdding === bookId
                    }
                    onClick={!user ? redirectLoginPage : handleAddToCartClicked}
                    data-meaning={cartMessage}
                  >
                    <CartLogo />
                  </button>
                )}
                <button
                  type="button"
                  className="removewishlt"
                  onClick={
                    !user
                      ? wishListRedirectToLoginPage
                      : wishlist
                      ? handleRemoveWishlistItem
                      : handleAddToWishListClicked
                  }
                  disabled={
                    wishlisting === bookId ||
                    wishlistRemoving === wishlistItemId
                  }
                  data-meaning={
                    wishlistedNow
                      ? wishlist
                        ? 'Delete'
                        : 'Added to Wishlist'
                      : 'Wishlist'
                  }
                >
                  {wishlistedNow ? (
                    wishlist ? (
                      <CartDelete />
                    ) : (
                      <WishlistLogoFilled />
                    )
                  ) : (
                    <WishlistLogo />
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
        <ReactTooltip id="read-check" place="bottom">
          <span>Read</span>
        </ReactTooltip>
        {alreadyRead && (
          <div className="alreadyread" data-tip={true} data-for="read-check">
            <i className="fa fa-check" />
          </div>
        )}
      </div>
    </div>
  );
};

export default BookSliderItem;
